// SCR-STAFF-906 — تنظیمات عمومی (US-PLT-071): واحد نمایش پول (ریال/تومان) با پیش‌نمایش نمونه و منطقه زمانی پیش‌فرض؛ ذخیره در
// Configuration (PUT /configuration/settings/{key}، دامنه Tenant). فقط لایه نمایش تغییر می‌کند (BR-PLT-080، AC-PLT-071.1).
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type SettingView } from '@phoenix/api-client';
import { useAccess, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DEFAULT_TIME_ZONE,
  FormPage,
  Money,
  SelectRHF,
  supportedTimeZones,
  type MoneyDisplayUnit,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { PLT_DISPLAY_SETTINGS_GROUP, pltKeys, WriteNotice } from './common';

export const SCR_STAFF_906 = 'SCR-STAFF-906';

export const CURRENCY_UNIT_KEY = 'plt.display_currency_unit';
export const TIME_ZONE_KEY = 'plt.default_timezone';

/** مبلغ نمونه پیش‌نمایش (ریال). */
export const PREVIEW_AMOUNT_MINOR = 12_345_670;

interface GeneralForm {
  currencyUnit: MoneyDisplayUnit;
  timeZone: string;
}

function find(items: readonly SettingView[] | undefined, key: string) {
  return items?.find((item) => item.key === key);
}

export function GeneralSettingsPage() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const write = useWriteAccess('plt.settings.manage');
  const canRead = access.can('cfg.settings.view');
  const [saved, setSaved] = useState(false);

  const settings = useQuery({
    queryKey: pltKeys.settings(session?.tenantId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/configuration/settings', {
          params: { query: { group: PLT_DISPLAY_SETTINGS_GROUP } },
        }),
      ),
    enabled: canRead,
  });

  const unitSetting = find(settings.data?.items, CURRENCY_UNIT_KEY);
  const zoneSetting = find(settings.data?.items, TIME_ZONE_KEY);
  const values = useMemo<GeneralForm>(
    () => ({
      currencyUnit: unitSetting?.value === 'Toman' ? 'Toman' : 'Rial',
      timeZone:
        typeof zoneSetting?.value === 'string' && zoneSetting.value ? zoneSetting.value : DEFAULT_TIME_ZONE,
    }),
    [unitSetting, zoneSetting],
  );
  const { control, handleSubmit, formState } = useForm<GeneralForm>({ values });
  const unit = useWatch({ control, name: 'currencyUnit' });

  // ویرایش: Permission plt.settings.manage، حالت Full و canEdit تنظیم از سرور.
  const editable = write.canWrite && (unitSetting?.canEdit ?? false) && (zoneSetting?.canEdit ?? false);

  const save = useMutation({
    mutationFn: async (form: GeneralForm) => {
      const changes: [string, string][] = [];
      if (form.currencyUnit !== values.currencyUnit) {
        changes.push([CURRENCY_UNIT_KEY, form.currencyUnit]);
      }
      if (form.timeZone !== values.timeZone) {
        changes.push([TIME_ZONE_KEY, form.timeZone]);
      }
      for (const [key, value] of changes) {
        await unwrap(
          tenantApi.PUT('/api/v1/configuration/settings/{key}', {
            params: { path: { key } },
            body: { scope: 'Tenant', scopeId: null, value },
          }),
        );
      }
    },
    onMutate: () => setSaved(false),
    onSuccess: async () => {
      // StaffLayout واحد نمایش و منطقه زمانی را از همین Query می‌خواند ← همه Money و JalaliDateها به‌روز می‌شوند.
      await queryClient.invalidateQueries({ queryKey: pltKeys.settings(session?.tenantId) });
      setSaved(true);
    },
  });

  return (
    <FormPage
      screenId={SCR_STAFF_906}
      titleKey="plt:general.title"
      subtitleKey="plt:general.subtitle"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={save.error ?? undefined}
      isLoading={canRead && settings.isPending}
      loadError={settings.error}
      readOnly={!editable}
      notice={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          {!canRead && <Alert severity="info">{t('plt:general.defaults_only')}</Alert>}
          {saved && !formState.isDirty && (
            <Alert severity="success" role="status" data-testid="save-success">
              {t('plt:general.saved')}
            </Alert>
          )}
        </Stack>
      }
    >
      <Controller
        control={control}
        name="currencyUnit"
        render={({ field }) => (
          <FormControl disabled={!editable}>
            <FormLabel id="currency-unit-label">{t('plt:general.currency_unit')}</FormLabel>
            <RadioGroup
              row
              aria-labelledby="currency-unit-label"
              name={field.name}
              value={field.value}
              onChange={(event) => field.onChange(event.target.value as MoneyDisplayUnit)}
              data-testid="currency-unit"
            >
              <FormControlLabel value="Rial" control={<Radio />} label={t('plt:general.unit.Rial')} />
              <FormControlLabel value="Toman" control={<Radio />} label={t('plt:general.unit.Toman')} />
            </RadioGroup>
          </FormControl>
        )}
      />
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="caption" color="text.secondary" component="div">
          {t('plt:general.preview')}
        </Typography>
        <Typography data-testid="currency-preview">
          <Money amountMinor={PREVIEW_AMOUNT_MINOR} unit={unit} />
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {t('plt:general.current')}{' '}
          <span data-testid="current-money">
            <Money amountMinor={PREVIEW_AMOUNT_MINOR} />
          </span>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('plt:general.preview_note')}
        </Typography>
      </Paper>
      <SelectRHF
        control={control}
        name="timeZone"
        labelKey="plt:general.time_zone"
        helperKey="plt:general.time_zone_help"
        fullWidth
        disabled={!editable}
        options={supportedTimeZones(values.timeZone).map((zone) => ({ value: zone, label: zone }))}
      />
    </FormPage>
  );
}
